import { RightSmArrow } from '@/components/icons/right-sm-arrow';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Typography } from '@/components/ui/typography';

import {
  RecommendationType,
  useRecommendationContent,
} from './use-recommendation-content';

interface CardProps {
  recommendationType: RecommendationType;
  onClick: () => void;
  value?: number;
}

export function Card({ recommendationType, onClick, value }: CardProps) {
  const { getRecommendationContent } = useRecommendationContent();
  const copy = getRecommendationContent(recommendationType, value);

  if (!copy) return null;

  const { icon: Icon } = copy;

  return (
    <>
      <div className="shadow-base flex w-80 flex-col items-start rounded-lg">
        {/* Header section of the card */}
        <div className="bg-fx-blue-50 flex items-center gap-2 self-stretch rounded-t-lg px-2 pt-2 pb-4">
          <Icon className="text-fx-blue" />
          <Typography variant="caption" className="pt-1">
            {copy.headerText}
          </Typography>
        </div>

        {/* Content section with stacking text */}
        <div className="-mt-2 flex flex-col items-start justify-center gap-2 self-stretch rounded-lg bg-white p-4">
          <div className="flex flex-col items-start gap-1 self-stretch">
            <Typography variant="body2">{copy.title}</Typography>
            <Typography variant="subtitle1" className="text-muted-foreground">
              {copy.description}
            </Typography>
          </div>

          <Button variant="secondary" className="mt-2 w-full" onClick={onClick}>
            <Typography variant="body2">{copy.buttonText}</Typography>
            <RightSmArrow />
          </Button>
        </div>
      </div>
    </>
  );
}

Card.Skeleton = function RecommendationCardSkeleton() {
  return (
    <div className="shadow-base flex w-80 flex-col items-start rounded-lg bg-white">
      {/* Header Section */}
      <div className="flex items-center gap-2 self-stretch px-2 pt-2 pb-4">
        <Skeleton className="h-5 w-5 rounded-full" />
        <Skeleton className="h-4 w-40" />
      </div>

      {/* Content Section */}
      <div className="-mt-2 flex flex-col items-start justify-center gap-2 self-stretch rounded-lg bg-white p-4">
        <div className="flex w-full flex-col gap-1 self-stretch">
          <Skeleton className="h-5 w-3/4" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-1/2" />
        </div>
        <Skeleton className="mt-2 h-9 w-full rounded-md" />
      </div>
    </div>
  );
};
